/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/

import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Formik } from "formik";
import { useHistory, useLocation } from 'react-router-dom';
import _ from 'lodash';

import BrokingSummary from 'components/BrokingSummary';
import { Col, Row } from 'components/Grid';
import Layout from 'components/Layout';
import Colors from 'constants/Colors';
import { GlobalAuthContext, GlobalLoaderContext } from 'store/context';
import { FitinsurAPI, MessageCenter } from 'utils';
import Button, { ButtonContainer, ButtonContent } from '@fitinsur/components/Button';
import { Icon, Loader, DragZoneUpload, FormContainer} from '@fitinsur/components';
import { Text, Content, ListContent } from './styles';
import DialogComponent from 'components/Dialog';
import {
  LoaderContext
} from '@fitinsur/contexts'


import ListDetail from './listDetail';
import ListHeader from './listHeader';

export default function DocumentManagement({ permissions, ...props }) {
  const { updateIsLoadingStatus } = useContext(LoaderContext);
  const columns = [
    {
      title: "Nome",
      value: "nmParty",
      col: 2,
      style: ''
    },
    {
      title: "Versão",
      value: "nmInterest",
      col: 1,
      style: ''
    },
    {
      title: "Tipo",
      value: "documentType",
      col: 2,
      style: ''
    },
    {
      title: "Feito Por",
      value: "uploadBy",
      col: 2,
      style: ''
    },
    {
      title: "Data",
      value: "date",
      col: 2,
      style: ''
    },
    {
      title: "Data Vencimento",
      value: "documentExpireDate",
      col: 2,
      style: ''
    },
    {
      title: "Ação",
      value: "action",
      col: 1,
      style: 'pr-0'
    },

  ];

  const authContext = useContext(GlobalAuthContext);
  const isClient = useMemo(() => authContext.enumProfile === 1, []);
  const [summary, setSummary] = useState({});
  const [shouldRenderOperationFilter, setShouldRenderOperationFilter] = useState(false);
  const [operationsList, setOperationsList] = useState([]);
  const [sortList, setSortList] = useState([]);
  const [filter, setFilter] = useState(
    {
      key: null,
      direction: null,
    }
  );
  const [data, setData] = useState([/* { nmParty: '', nmInterest: '', documentType: '', uploadBy: '', date: '', action: '' } */]);
  const history = useHistory();
  const fitinsur = new FitinsurAPI();
  const mc = new MessageCenter();
  const [documents, setDocuments] = useState([]);
  const [documentsExpired, setDocumentsExpired] = useState([]);
  const location = useLocation();
  const [dialog, setDialog] = useState({
    visible: false,
    title: '',
    icon: 'exclamation',
    args: null
  });

  useEffect(() => {
    if (authContext.status !== true) {
      mc.error("User is not authenticated, please Login");
      updateIsLoadingStatus(false);
      goToLoginPage();
      return;
    } else {
      fitinsur.setAuthToken(authContext.token);
      updateIsLoadingStatus(false);
    };
    handleSummary();
    getDocumentOperations();

    if (location.search) {
      handleSetFilterFromParams()
      getFiles(location.search)
    } else {
      getFiles();
    }
  }, []);

  useMemo(() => {
    setSortList(data);
    if (sortList) {
      sortList.sort((a, b) => {
        if (a[filter.key] < b[filter.key]) {
          return filter.direction === 'asc' ? -1 : 1;
        } else if (a[filter.key] > b[filter.key]) {
          return filter.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
  }, [filter, data]);

  //---------------------------
  //-- data of header
  //---------------------------
  function handleSummary() {
    fitinsur.setAuthToken(authContext.token);
    fitinsur.requestHeaderNbi(props.match.params.idOperation)
      .then(response => response.json()).then(data => {
        setSummary({
          typeOfCover: data.ret_value.typeOfCover,
          client: data.ret_value.client,
          obligorCoding: data.ret_value.obligorCoding,
          responseBy: data.ret_value.responseBy,
          internalContact: data.ret_value.internalContact,
          broker: data.ret_value.broker,
          dealCode: data.ret_value.deal,
          nmFacility: data.ret_value.nmFacility,
          currency: data.ret_value.currency,
          nmObligor: data.ret_value.nmObligor,
          idOperation: props.match.params.idOperation,
        });
      })
      .catch((error) => {
        mc.error("There was an error while fetching your data: \n" + error);
        console.error('fitPostSummary - Error:', error);
      });
  };

  const getFiles = (filterQueryString = '') => {
    updateIsLoadingStatus(true);
    fitinsur.setAuthToken(authContext.token);

    fitinsur.requestGetDealDocuments(
      props.match.params.idOperation,
      filterQueryString ? filterQueryString + '&idQuestion=694' : '?idQuestion=694'
    ).then(response => response.json()).then(data => {
      if (data.ret_code != 200) {
        mc.error(data.ret_msg_details);
        history.goBack()
        return;
      }
      setDocuments(data.ret_value);
      updateIsLoadingStatus(false);
    })
  }

  const showFilesExpired = (idDomain) => {
    updateIsLoadingStatus(true);
    fitinsur.setAuthToken(authContext.token);

    fitinsur.requestGetDealDocuments(props.match.params.idOperation, `?expired=true&idDomain=${idDomain}`)
      .then(response => response.json()).then(data => {
        if (data.ret_code != 200) {
          mc.error(data.ret_msg_details);
          history.goBack()
          return;
        }
        setDocumentsExpired(data.ret_value);
        setDialog(prev => ({ ...prev, visible: true }))
        updateIsLoadingStatus(false);
      })
  }

  const getDocumentOperations = () => {
    updateIsLoadingStatus(true);
    fitinsur.setAuthToken(authContext.token);

    fitinsur.requestListDocumentOperations(props.match.params.idOperation)
      .then(response => response.json())
      .then(data => {
        if (data.ret_code !== 200) {
          mc.error(data.ret_msg_details);
          return;
        }

        if (!data.ret_value.isMasterPolicy && data.ret_value.operationsList) setShouldRenderOperationFilter(true)
        setOperationsList(data.ret_value.operationsList.map(operation => ({
          label: operation.referenceNumber,
          value: operation.idOperation,
        })))

      })
  }

  function handleSortList(key) {
    let direction = 'asc';
    if (filter.key === key && filter.direction === 'asc') {
      direction = 'desc';
    }
    setFilter({ key: key, direction: direction });

    console.log("Lista Ordenada-> ", sortList);
    console.log("Ordem key-> ", filter.direction);
  }

  const goToLoginPage = () => {
    history.push('/login');
  };

  function handleBack() {
    history.goBack();
  };

  const fieldRenderOptions = function (options = [], idDomainStatusFilter) {
    if(options.length){
      const optionsList = options.map(option => {
        let behavior = {};

        try {
          behavior = JSON.parse(option.ANSWER_BEHAVIOR) || {};
        } catch (e) {
          console.error('fieldRenderOptions - Error in parse Behavior')
        }

        if (idDomainStatusFilter) {
          if (
            !Array.isArray(behavior?.idDomainStatusFilter) &&
            behavior?.idDomainStatusFilter !== idDomainStatusFilter
          ) {
            return null;
          } else if (
            Array.isArray(behavior?.idDomainStatusFilter) &&
            !behavior?.idDomainStatusFilter.includes(idDomainStatusFilter)
          ) {
            return null;
          }
        }
        return {
          label: option.ANSWER_OPTION_DESCRIPTION,
          value: {
            idAnswerOption: option.ID_ANSWER_OPTION,
            dsAnswerOption: option.ANSWER_OPTION_DESCRIPTION,
            behavior,
          }
        }
      });

      return optionsList.filter(Boolean)
    }
    return [];
  }

  function renderDocuments(title, nameArray, hasUpload, hasDelete, history = false, expired = false) {
    const documentList = expired ? documentsExpired : documents
    return (
      <>
        <FormContainer title={title}>
          <Row>
            {columns ? columns.map((column, index) =>
              <Col sm={column.col} key={index} className={'d-none d-md-block pl-0 ' + column.style}>
                <Text onClick={() => handleSortList(column.value)}>
                  {column.title}
                </Text>
                {column.title == 'Action' ? null : filter.key === column.value && filter.direction === "asc" ?
                  <Icon name={"arrowup"} /> : <Icon name={"arrowdown"} />}
              </Col>) : ""}

          </Row>
          {documentList[nameArray] && documentList[nameArray].map((document, index) => {
            return (
              <React.Fragment key={index}>
                <ListDetail
                  document={document}
                  idOperation={props.match.params.idOperation}
                  listDocuments={getFiles}
                  hasDelete={hasDelete}
                  columns={columns}
                  isClient={isClient}
                  nameArray={nameArray}
                  permissions={permissions}
                  onHistoryClick={history ? ({ idDomain }) => showFilesExpired(idDomain) : false}
                  hiddenTrash={expired}
                />
              </React.Fragment>
            )
          })}
        </FormContainer>
      </>
    );
  }

  const jsonToQueryString = (json) => {
    const keys = Object.keys(json).filter(key => json[key]);

    return keys
      .map((key, index) => {
        const sufix = index + 1 === keys.length
          ? ''
          : '&'

        return encodeURIComponent(key) + '=' + encodeURIComponent(json[key]) + sufix;
      }
      ).join('');
  }


  const handleSetFilterFromParams = () => {
    if (location.search) {
      const query = new URLSearchParams(location.search);

      if (query.get('search')) {
        filterFormik.current.setFieldValue('search', query.get('search'))
      }

      if (query.get('idOperation')) {
        filterFormik.current.setFieldValue('idOperation', Number(query.get('idOperation')))
      }

    }
  }

  const handleFilter = (filters = {}) => {

    const filterQueryString = `?${jsonToQueryString(filters)}`;

    getFiles(filterQueryString);
    history.push(`${filterQueryString}`)
  }

  let filterFormik = null;

  return (
    <Layout
      title={"DOCUMENTOS"}
      subtitle={props.match?.params?.idOperation}
    >
      <BrokingSummary
        history={history}
        idOperation={props.match?.params?.idOperation}
        idProduct={props.match?.params?.idProduct} />
      <Content>
        <ListHeader
          title="DOCUMENTOS"
          formikRef={ref => filterFormik = ref}
          ListHeader
          operationFilter={shouldRenderOperationFilter}
          operationsList={operationsList}
          updateFilter={(filters) => {
            handleFilter(filters)
          }}
        />

        <DialogComponent title={dialog.title} size='1000px' show={dialog.visible} setDialog={setDialog} center>
          <div style={{ margin: 30 }}>
            {renderDocuments('VERSÕES ANTERIORES', 'dealDocuments', false, false, false, true)}
            <ButtonContainer className='justify-content-end'>
              <ButtonContent>
                <Button
                  id="btnContinue"
                  type="button"
                  style={{ margin: '0 0 15px 15px' }}
                  onClick={() => setDialog(prev => ({ ...prev, visible: false }))}
                  third
                >
                  FECHAR
                </Button>
              </ButtonContent>
            </ButtonContainer>
          </div>
        </DialogComponent>

        <ListContent>
          {/* {renderDocuments('Signed Documents', 'signedDocuments', false, false)} */}
          {permissions.DOCUMENT_GENERAL?.READ && renderDocuments('PROCESSO', 'dealDocuments', false, false, true)}
          {permissions.DOCUMENT_GENERAL?.READ && renderDocuments('SUPORTE', 'supportDocuments', false, permissions.DOCUMENT_GENERAL?.DELETE)}
          {permissions.DOCUMENT_GENERAL?.READ && !isClient ? renderDocuments('INTERNOS', 'internalDocuments', false, permissions.DOCUMENT_GENERAL?.DELETE) : null}

          <Formik
            enableReinitialize={true}
            initialValues={{}}
          >
            {({ values, setFieldValue, validationSchema }) => (
              permissions.DOCUMENT_GENERAL?.CREATE && <DragZoneUpload multiple={true}
                s3upload
                options={fieldRenderOptions(documents['documentTypes'], 7)}
                documentTypeRequired={true}
                documentType={values.documentType}
                idOperation={props.match.params.idOperation}
                isDraft={false}
                onSuccess={() => {
                  updateIsLoadingStatus(true);
                  getFiles();
                }}
              />
            )}
          </Formik>
          <ButtonContainer>
            <Button forth onClick={() => handleBack()}>&lt; Voltar</Button>
          </ButtonContainer>
        </ListContent>
      </Content>
    </Layout>
  )
}